/**
 * The grid's clipboard rules (owner, 07.10.2026: a pretty-printed JSON value
 * pasted into ONE cell was split line by line down the rows below it — "make
 * sure this never happens").
 *
 * Three rules, the ones Excel / Google Sheets / DataGrip follow:
 *
 * 1. A text field owns its clipboard. A paste, copy or cut whose target is an
 *    input, a textarea, a select, a contenteditable or a Monaco editor is the
 *    field's — never the grid's, selection or not.
 * 2. Text is spread over several cells only when it IS a grid: tab-separated
 *    and rectangular, or one value per line with exactly as many lines as rows
 *    selected. Anything else — JSON, an SQL snippet, prose — is ONE value.
 * 3. A multi-cell copy quotes a value that holds a tab, a line break or a
 *    leading quote ("…", inner quotes doubled), so copy → paste puts it back
 *    in one cell. A single-cell copy stays raw: pasted into a text editor, a
 *    JSON value must read as JSON, not as a quoted string.
 */

const EDITABLE = 'input, textarea, select, [contenteditable=""], [contenteditable="true"], .monaco-editor';

export function fieldOwnsClipboard(target) {
  if (!target || typeof target.closest !== 'function') return false;
  return Boolean(target.closest(EDITABLE));
}

export function toClipboardField(text) {
  const s = text == null ? '' : String(text);
  if (!/[\t\r\n]/.test(s) && !s.startsWith('"')) return s;
  return `"${s.replace(/"/g, '""')}"`;
}

/**
 * Tab-separated text → rows of fields, with Excel's quoting. Returns null when
 * the text is not well-formed TSV (a quoted field followed by anything but a
 * tab or a line end) — `{"a": 1}` is a value, not a malformed grid.
 */
export function parseClipboardGrid(text) {
  const s = String(text).replace(/\r\n?/g, '\n').replace(/\n$/, '');
  const rows = [];
  let row = [];
  let i = 0;
  for (;;) {
    let field = '';
    if (s[i] === '"') {
      i += 1;
      for (;;) {
        if (i >= s.length) return null; // unterminated quote
        if (s[i] === '"') {
          if (s[i + 1] === '"') {
            field += '"';
            i += 2;
            continue;
          }
          i += 1;
          break;
        }
        field += s[i];
        i += 1;
      }
      if (i < s.length && s[i] !== '\t' && s[i] !== '\n') return null;
    } else {
      while (i < s.length && s[i] !== '\t' && s[i] !== '\n') {
        field += s[i];
        i += 1;
      }
    }
    row.push(field);
    if (i >= s.length) break;
    if (s[i] === '\n') {
      rows.push(row);
      row = [];
    }
    i += 1;
  }
  rows.push(row);
  return rows;
}

/**
 * What a paste does: `{ mode: 'block', block }` lays a rectangle from the
 * anchor cell, `{ mode: 'fill', value }` writes one value into every selected
 * cell. `selectedRows` is how many rows the selection spans.
 */
export function planPaste(text, { selectedRows = 1 } = {}) {
  const raw = text == null ? '' : String(text);
  const fill = { mode: 'fill', value: raw };
  if (!/[\t\r\n]/.test(raw)) return fill;
  const rows = parseClipboardGrid(raw);
  if (!rows) return fill;
  const width = rows[0].length;
  if (rows.some((r) => r.length !== width)) return fill;
  if (rows.length === 1 && width === 1) return { mode: 'fill', value: rows[0][0] };
  if (width > 1) return { mode: 'block', block: rows };
  // One column, several lines: a column only when it matches the selection.
  if (rows.length === selectedRows) return { mode: 'block', block: rows };
  return fill;
}
